import {
    ApiContentSection,
    ApiFullLandingPageLink,
} from '@storefronts/common-types'
import React, { useContext, useEffect, useRef } from 'react'
import {
    OverrideContext,
    SectionTreeScopeContext,
} from '../../../context/overrideContext'
import { PageContext } from '../../../context/PageContextProvider'
import { Constants } from '../../../style/styleConstants'
import {
    getFirstLcpImageSectionIndex,
    getFirstPaintImageSectionIndex,
    isSectionBelowFold,
} from '../../../utils/getFirstLcpImageSectionIndex'
import { SelectedSectionIdxContext } from './SectionShell'
import { renderSection } from './sectionRenderers'

export const RenderSections: React.FC<{
    sections: ApiContentSection[]
    /**
     * When true, signals that sections are rendered inside a PDP body context.
     * Currently only used by ReviewsSection (to add breathing room between the
     * PDP hero and the first review block) and by CTA_BUTTON (which renders
     * inline instead of padded). All other sections render identically.
     */
    compact?: boolean
    /** Already-resolved product for a reviews section with a matching hash. */
    reviewsProductLink?: ApiFullLandingPageLink
    /**
     * True when these sections belong to something other than the top-level
     * landing page (currently only the PDP body inside an inline product). The
     * admin editor keys section picks off `data-editor-section-index`, and
     * those indexes address the landing page's section list — nested indexes
     * would resolve to unrelated sections. Left un-stamped so a click inside
     * a nested render resolves to the outermost stamped ancestor instead.
     */
    nested?: boolean
}> = ({ sections, compact, reviewsProductLink, nested = false }) => {
    const sectionRefs = useRef<Array<HTMLElement | null>>([])
    const { selectedBlockIdx, selectedBlockScope } = useContext(OverrideContext)
    const scope = useContext(SectionTreeScopeContext)
    const { activeLPLink } = useContext(PageContext)
    const stampInspectAttrs = !nested

    const selectedIdx =
        selectedBlockScope === scope ? selectedBlockIdx : undefined

    // Only for Admin Editor
    useEffect(() => {
        if (selectedIdx === undefined || selectedIdx < 0) return
        // Nested renders (currently the PDP body inside an inline product)
        // address a different section list; a match here would scroll to an
        // unrelated section on the outer page.
        // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
        if (nested) return
        const elt = sectionRefs.current[selectedIdx]
        if (elt) {
            const elementTop = elt.getBoundingClientRect().top
            const offsetPosition =
                elementTop + window.scrollY - Constants.EDITOR_SCROLL_OFFSET
            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth',
            })
        }
    }, [selectedIdx, nested])

    if (sections.length === 0) return null

    const firstLcpImageSectionIndex = getFirstLcpImageSectionIndex(sections)
    const firstPaintImageSectionIndex = getFirstPaintImageSectionIndex(sections)

    const rendered = sections.map((section, index) => {
        const content = renderSection(section, {
            index,
            compact,
            reviewsProductLink,
            isBelowFold: isSectionBelowFold(index, firstPaintImageSectionIndex),
            prioritizeImages: index === firstLcpImageSectionIndex,
            sectionRef: (el) => {
                sectionRefs.current[index] = el
            },
            stampInspectAttrs,
            showRecentlyViewed: activeLPLink !== null,
        })
        if (content == null) return null
        return <React.Fragment key={index}>{content}</React.Fragment>
    })

    return (
        <SelectedSectionIdxContext.Provider value={selectedIdx}>
            <div>{rendered}</div>
        </SelectedSectionIdxContext.Provider>
    )
}
